import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiClock, FiChevronDown } from 'react-icons/fi';
import { useReminders } from '../../hooks/useReminders';

const REMINDER_TYPES_KEY = 'glowlytix_reminder_types';

function loadReminderTypes() {
  try { return JSON.parse(localStorage.getItem(REMINDER_TYPES_KEY) || '{}'); }
  catch { return {}; }
}

function saveReminderTypes(types) {
  localStorage.setItem(REMINDER_TYPES_KEY, JSON.stringify(types));
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function fmt12(h) {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

function getBestReminderHour(entries) {
  const real = (entries || []).filter(e => typeof e.id === 'number' && e.id > 1_000_000_000_000);
  if (real.length >= 3) {
    const freq = {};
    real.map(e => new Date(e.id).getHours()).forEach(h => { freq[h] = (freq[h] || 0) + 1; });
    return parseInt(Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0]);
  }
  return null;
}

function ReminderToggleRow({ title, description, enabled, onToggle }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <p className="font-sans text-sm font-semibold text-stone-800">{title}</p>
        <p className="font-sans text-xs text-stone-400 mt-0.5 leading-relaxed">{description}</p>
      </div>
      <button
        onClick={onToggle}
        className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-all duration-300 ${enabled ? 'bg-[#5C8A78]' : 'bg-stone-200'}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all duration-300 ${enabled ? 'left-6 translate-x-0.5' : 'left-0.5'}`} />
      </button>
    </div>
  );
}

export default function RemindersSettings({ entries }) {
  const { settings, permission, frequencies, enable, disable, updateSettings } = useReminders();
  const [showHourPicker, setShowHourPicker] = useState(false);
  const [permDenied, setPermDenied] = useState(false);
  const [reminderTypes, setReminderTypes] = useState(() => {
    const saved = loadReminderTypes();
    return {
      daily: saved.daily ?? settings.enabled,
      routine: saved.routine ?? false,
      progress: saved.progress ?? false,
    };
  });

  const suggestedHour = useMemo(() => getBestReminderHour(entries), [entries]);
  const isPersonalized = suggestedHour !== null;
  const displaySuggested = suggestedHour ?? 20;

  const anyEnabled = Object.values(reminderTypes).some(Boolean);

  const handleToggleType = async (type) => {
    const newVal = !reminderTypes[type];
    const updated = { ...reminderTypes, [type]: newVal };

    if (newVal && !settings.enabled) {
      const ok = await enable();
      if (!ok) {
        setPermDenied(true);
        return;
      }
      setPermDenied(false);
    }

    saveReminderTypes(updated);
    setReminderTypes(updated);

    const anyOn = Object.values(updated).some(Boolean);
    if (!anyOn && settings.enabled) {
      await disable();
    }
  };

  return (
    <div className="space-y-5 pb-6">
      <div>
        <h2 className="font-serif text-2xl text-stone-900 mb-1">Reminders</h2>
        <p className="font-sans text-xs text-stone-500">Get notified to log your skin and photos.</p>
      </div>

      {/* Permission denied warning */}
      {permDenied && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
          className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3">
          <p className="font-sans text-xs text-amber-700">
            Notifications are blocked. Please enable them in your browser settings, then try again.
          </p>
        </motion.div>
      )}

      {/* Three labeled reminder toggles */}
      <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-5 space-y-5">
        <ReminderToggleRow
          title="Daily Reminder"
          description="Get a reminder to log your skin daily"
          enabled={reminderTypes.daily}
          onToggle={() => handleToggleType('daily')}
        />
        <div className="border-t border-stone-100" />
        <ReminderToggleRow
          title="Routine Reminder"
          description="Get notified when it's time to apply your routine"
          enabled={reminderTypes.routine}
          onToggle={() => handleToggleType('routine')}
        />
        <div className="border-t border-stone-100" />
        <ReminderToggleRow
          title="Progress Check-ins"
          description="Weekly reminders to review your glow progress"
          enabled={reminderTypes.progress}
          onToggle={() => handleToggleType('progress')}
        />
      </div>

      {anyEnabled && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Smart suggestion */}
          <div className="rounded-2xl border border-violet-100 bg-violet-50 px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-sans text-xs font-semibold text-violet-700">
                {isPersonalized ? 'Your best time' : 'Popular logging time'}
              </p>
              <p className="font-sans text-xs text-violet-500 mt-0.5">
                {fmt12(displaySuggested)} · {isPersonalized ? 'based on your logging patterns' : 'most users log at 8 PM'}
              </p>
            </div>
            {settings.hour !== displaySuggested ? (
              <button onClick={() => updateSettings({ hour: displaySuggested })}
                className="font-sans text-xs font-semibold text-violet-700 bg-violet-100 px-3 py-1.5 rounded-full active:bg-violet-200 transition-colors flex-shrink-0">
                Use this
              </button>
            ) : (
              <span className="font-sans text-xs text-violet-500 font-medium flex-shrink-0">✓ Set</span>
            )}
          </div>

          {/* Frequency */}
          <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-5">
            <p className="font-sans text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3">Frequency</p>
            <div className="grid grid-cols-2 gap-2.5">
              {frequencies.map(f => (
                <button key={f.id} onClick={() => updateSettings({ frequency: f.id })}
                  className={`rounded-2xl py-3 px-4 font-sans text-sm font-medium transition-all active:scale-95 text-left ${
                    settings.frequency === f.id ? 'text-white' : 'bg-stone-50 border border-stone-200 text-stone-700'
                  }`}
                  style={settings.frequency === f.id ? { background: '#5C8A78' } : {}}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Time picker */}
          <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-5">
            <p className="font-sans text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3">Reminder Time</p>
            <button onClick={() => setShowHourPicker(v => !v)}
              className="w-full flex items-center justify-between bg-stone-50 border border-stone-200 rounded-2xl px-4 py-3.5">
              <div className="flex items-center gap-2.5">
                <SafeIcon icon={FiClock} className="text-stone-400" />
                <span className="font-sans text-sm font-semibold text-stone-800">{fmt12(settings.hour)}</span>
              </div>
              <SafeIcon icon={FiChevronDown} className={`text-stone-400 transition-transform ${showHourPicker ? 'rotate-180' : ''}`} />
            </button>

            {showHourPicker && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                className="mt-3 grid grid-cols-4 gap-2">
                {HOURS.map(h => (
                  <button key={h} onClick={() => { updateSettings({ hour: h }); setShowHourPicker(false); }}
                    className={`rounded-xl py-2.5 font-sans text-xs font-semibold transition-all active:scale-95 ${
                      settings.hour === h ? 'text-white' : 'bg-stone-50 border border-stone-200 text-stone-600'
                    }`}
                    style={settings.hour === h ? { background: '#5C8A78' } : {}}>
                    {fmt12(h)}
                  </button>
                ))}
              </motion.div>
            )}
          </div>

          <div className="rounded-2xl bg-[#5C8A7810] border border-[#5C8A7830] px-4 py-3">
            <p className="font-sans text-xs text-[#5C8A78]">
              You'll be reminded {frequencies.find(f => f.id === settings.frequency)?.label.toLowerCase()} at {fmt12(settings.hour)} to log your skin, routine, and photos.
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
}
